import React from "react";

import {
    Route,
    Redirect,
} from "react-router-dom";

import useAuth
    from "../hooks/useAuth";

import DashboardLayout
    from "../components/dashboard/DashboardLayout";


/*
|--------------------------------------------------------------------------
| NORMALIZE PERMISSION
|--------------------------------------------------------------------------
*/

const normalizePermission = (
    value
) => {

    return String(
        value ||
        ""
    )
        .trim()
        .toLowerCase();

};


/*
|--------------------------------------------------------------------------
| NORMALIZE ROLE CODE
|--------------------------------------------------------------------------
*/

const normalizeRoleCode = (
    value
) => {

    return String(
        value ||
        ""
    )
        .trim()
        .toUpperCase();

};


/*
|--------------------------------------------------------------------------
| AUTH GUARD
|--------------------------------------------------------------------------
*/

const AuthGuard = ({

    component:
        Component,

    /*
    |--------------------------------------------------------------------------
    | LAYOUT OPTIONS
    |--------------------------------------------------------------------------
    */

    showSidebar =
        true,

    showHeader =
        true,

    showChat =
        true,

    defaultSidebarCollapsed =
        false,

    /*
    |--------------------------------------------------------------------------
    | AUTHORIZATION OPTIONS
    |--------------------------------------------------------------------------
    |
    | permission:
    |
    |   "sale_invoice.view"
    |
    |
    | permissions:
    |
    |   [
    |       "sale_invoice.view",
    |       "sale_invoice.create"
    |   ]
    |
    |
    | requireAll:
    |
    | false => ANY permission
    | true  => ALL permissions
    |
    |
    | ownerOnly:
    |
    | true => only tenant OWNER
    |
    |--------------------------------------------------------------------------
    */

    permission =
        null,

    permissions =
        [],

    requireAll =
        false,

    ownerOnly =
        false,

    /*
    |--------------------------------------------------------------------------
    | FALLBACK
    |--------------------------------------------------------------------------
    */

    unauthorizedRedirect =
        "/dashboard",

    /*
    |--------------------------------------------------------------------------
    | ROUTE OPTIONS
    |--------------------------------------------------------------------------
    */

    ...rest

}) => {

    /*
    |--------------------------------------------------------------------------
    | AUTH CONTEXT
    |--------------------------------------------------------------------------
    */

    const {

        loading,

        isAuthenticated,

        user,

        role,

        permissions:
            rawPermissions = [],

        permissionCodes:
            contextPermissionCodes = [],

        isOwner,

    } =
        useAuth();


    /*
    |--------------------------------------------------------------------------
    | OWNER DETECTION
    |--------------------------------------------------------------------------
    |
    | IMPORTANT:
    |
    | Owner may exist in different places depending on login/profile response:
    |
    | 1. useAuth().isOwner
    | 2. user.userType
    | 3. role.code
    | 4. user.role.code
    | 5. user.roleCode
    |
    |--------------------------------------------------------------------------
    */

    const owner =
        Boolean(
            isOwner
        ) ||

        normalizeRoleCode(
            user
                ?.userType
        ) ===
            "OWNER" ||

        normalizeRoleCode(
            role
                ?.code
        ) ===
            "OWNER" ||

        normalizeRoleCode(
            user
                ?.role
                ?.code
        ) ===
            "OWNER" ||

        normalizeRoleCode(
            user
                ?.roleCode
        ) ===
            "OWNER";


    /*
    |--------------------------------------------------------------------------
    | CURRENT PERMISSION CODES
    |--------------------------------------------------------------------------
    */

    const normalizedUserPermissions =
        Array.isArray(
            contextPermissionCodes
        ) &&
        contextPermissionCodes.length >
            0
            ?
            [
                ...new Set(
                    contextPermissionCodes
                        .map(
                            normalizePermission
                        )
                        .filter(
                            Boolean
                        )
                ),
            ]
            :
            [
                ...new Set(
                    (
                        Array.isArray(
                            rawPermissions
                        )
                            ?
                            rawPermissions
                            :
                            []
                    )
                        .map(
                            (
                                permissionItem
                            ) => {

                                if (
                                    typeof permissionItem ===
                                    "string"
                                ) {

                                    return normalizePermission(
                                        permissionItem
                                    );

                                }


                                if (
                                    permissionItem &&
                                    typeof permissionItem ===
                                        "object"
                                ) {

                                    return normalizePermission(
                                        permissionItem
                                            ?.code
                                    );

                                }


                                return "";

                            }
                        )
                        .filter(
                            Boolean
                        )
                ),
            ];


    /*
    |--------------------------------------------------------------------------
    | REQUIRED PERMISSIONS
    |--------------------------------------------------------------------------
    */

    const requiredPermissions = [

        ...(
            permission
                ?
                [
                    permission,
                ]
                :
                []
        ),

        ...(
            Array.isArray(
                permissions
            )
                ?
                permissions
                :
                []
        ),

    ]
        .map(
            normalizePermission
        )
        .filter(
            Boolean
        );


    /*
    |--------------------------------------------------------------------------
    | HAS REQUIRED PERMISSION
    |--------------------------------------------------------------------------
    */

    const hasRequiredPermission =
        () => {

            /*
            |--------------------------------------------------------------------------
            | OWNER BYPASS
            |--------------------------------------------------------------------------
            |
            | Tenant owner gets full frontend route access.
            |
            | Backend subscription/module middleware remains the real security
            | authority.
            |
            |--------------------------------------------------------------------------
            */

            if (
                owner
            ) {

                return true;

            }


            /*
            |--------------------------------------------------------------------------
            | NOTHING REQUIRED
            |--------------------------------------------------------------------------
            */

            if (
                requiredPermissions.length ===
                0
            ) {

                return true;

            }


            /*
            |--------------------------------------------------------------------------
            | ALL REQUIRED
            |--------------------------------------------------------------------------
            */

            if (
                requireAll
            ) {

                return requiredPermissions
                    .every(
                        (
                            requiredPermission
                        ) =>
                            normalizedUserPermissions
                                .includes(
                                    requiredPermission
                                )
                    );

            }


            /*
            |--------------------------------------------------------------------------
            | ANY REQUIRED
            |--------------------------------------------------------------------------
            */

            return requiredPermissions
                .some(
                    (
                        requiredPermission
                    ) =>
                        normalizedUserPermissions
                            .includes(
                                requiredPermission
                            )
                );

        };


    /*
    |--------------------------------------------------------------------------
    | ROUTE
    |--------------------------------------------------------------------------
    */

    return (

        <Route

            {...rest}

            render={
                (
                    props
                ) => {

                    /*
                    |--------------------------------------------------------------------------
                    | AUTH LOADING
                    |--------------------------------------------------------------------------
                    */

                    if (
                        loading
                    ) {

                        return (

                            <div className="auth-guard-loading">
                                Loading...
                            </div>

                        );

                    }


                    /*
                    |--------------------------------------------------------------------------
                    | NOT AUTHENTICATED
                    |--------------------------------------------------------------------------
                    */

                    if (
                        !isAuthenticated
                    ) {

                        return (

                            <Redirect
                                to="/"
                            />

                        );

                    }


                    /*
                    |--------------------------------------------------------------------------
                    | OWNER ONLY
                    |--------------------------------------------------------------------------
                    */

                    if (
                        ownerOnly &&
                        !owner
                    ) {

                        return (

                            <Redirect
                                to={
                                    unauthorizedRedirect
                                }
                            />

                        );

                    }


                    /*
                    |--------------------------------------------------------------------------
                    | PERMISSION CHECK
                    |--------------------------------------------------------------------------
                    */

                    if (
                        !hasRequiredPermission()
                    ) {

                        return (

                            <Redirect
                                to={
                                    unauthorizedRedirect
                                }
                            />

                        );

                    }


                    /*
                    |--------------------------------------------------------------------------
                    | AUTHORIZED
                    |--------------------------------------------------------------------------
                    */

                    return (

                        <DashboardLayout

                            showSidebar={
                                showSidebar
                            }

                            showHeader={
                                showHeader
                            }

                            showChat={
                                showChat
                            }

                            defaultSidebarCollapsed={
                                defaultSidebarCollapsed
                            }
                        >

                            <Component

                                {...props}

                                authUser={
                                    user
                                }

                                authRole={
                                    role
                                }

                                authPermissions={
                                    normalizedUserPermissions
                                }

                                isOwner={
                                    owner
                                }
                            />

                        </DashboardLayout>

                    );

                }
            }
        />

    );

};


export default
    AuthGuard;